import React, { useMemo, useState } from "react";
import {
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Box,
  Checkbox,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Alert,
  Tooltip,
  IconButton,
  Divider,
  FormControlLabel,
} from "@mui/material";
import {
  Transform as TransformIcon,
  Storage as StorageIcon,
  Code as CodeIcon,
  SortByAlpha as SortByAlphaIcon,
} from "@mui/icons-material";
import ModifierModal from "./modals/ModifierModal";

const IngestionMapper = ({ ingestionMappings, onIngestionMappingChange }) => {
  const [sortAsc, setSortAsc] = useState(true);
  const [modifierOpen, setModifierOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [modalState, setModalState] = useState({ modifier: "" });

  const getFieldTypeIcon = (targetField) => {
    if (targetField === "phone") return <CodeIcon color="info" sx={{ fontSize: 18 }} />;
    return null;
  };

  const getFieldRequirements = () => {
    // Removed specific field requirements as they're not needed in the ingestion mapper
    return null;
  };

  const sortedEntries = useMemo(() => {
    const entries = Object.entries(ingestionMappings);
    return entries
      .slice()
      .sort((a, b) => a[1].sourceField.localeCompare(b[1].sourceField))
      .map((e) => e)
      [sortAsc ? "slice" : "reverse"]();
  }, [ingestionMappings, sortAsc]);

  const handleRowDoubleClick = (sourceKey, mapping) => {
    // If mapping is fixed and not default, keep it non-editable like before
    if (mapping.isFixed && !mapping.isDefault) return;
    setSelectedKey(sourceKey);
    setModalState({ modifier: mapping.modifierFunction || "" });
    setModifierOpen(true);
  };

  const handleModifierSubmit = () => {
    if (!selectedKey) return;
    onIngestionMappingChange(selectedKey, "modifierFunction", modalState.modifier || "");
    setModifierOpen(false);
    setSelectedKey(null);
  };

  const handleModalStateChange = (key, value) => {
    setModalState((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
        Ingestion Mapper
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
        Configure how source data fields map to the ingestion schema. Define field transformations
        and specify which fields should be included in the ingestion process.
      </Typography>

      <Card elevation={1}>
        <CardHeader
          avatar={<TransformIcon color="secondary" />}
          title="Field Mapping Configuration"
          subheader={`${Object.keys(ingestionMappings).length} source fields available for mapping`}
          slotProps={{
            title: { variant: "h6", fontWeight: 600 },
          }}
        />
        <Divider />
        <CardContent sx={{ p: 0 }}>
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="medium" stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: "grey.50" }}>
                  <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <StorageIcon fontSize="small" />
                      Source Field
                      <Tooltip title={`Sort ${sortAsc ? "A→Z" : "Z→A"}`}>
                        <IconButton size="small" onClick={() => setSortAsc((s) => !s)}>
                          <SortByAlphaIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 250 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TransformIcon fontSize="small" />
                      Target Mapping
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 120 }}>Include Field</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedEntries.map(([sourceKey, mapping]) => {
                  const fieldReq = getFieldRequirements();

                  return (
                    <TableRow
                      key={sourceKey}
                      hover
                      onDoubleClick={() => handleRowDoubleClick(sourceKey, mapping)}
                      sx={{ "&:hover": { backgroundColor: "grey.25" }, cursor: "pointer" }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                            {mapping.sourceField}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            {mapping.isFixed && (
                              <Chip
                                size="small"
                                label="Fixed Mapping"
                                color="primary"
                                variant="outlined"
                                sx={{ fontSize: "0.7rem" }}
                              />
                            )}
                            {mapping.isDefault && (
                              <Chip
                                size="small"
                                label="Default Value"
                                color="primary"
                                variant="outlined"
                                sx={{ fontSize: "0.7rem" }}
                              />
                            )}
                            {mapping.modifierFunction && (
                              <Chip
                                size="small"
                                label="Has Modifier"
                                color="secondary"
                                variant="outlined"
                                icon={<CodeIcon sx={{ fontSize: 16 }} />}
                                sx={{ fontSize: "0.7rem" }}
                              />
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <TextField
                            fullWidth
                            size="small"
                            value={mapping.targetField}
                            onChange={(e) =>
                              onIngestionMappingChange(sourceKey, "targetField", e.target.value)
                            }
                            disabled={mapping.isFixed}
                            sx={{ mb: fieldReq ? 1 : 0 }}
                            slotProps={{
                              input: {
                                endAdornment: getFieldTypeIcon(mapping.targetField),
                              },
                            }}
                          />
                          {fieldReq && (
                            <Alert severity={fieldReq.severity} sx={{ py: 0.5 }}>
                              <Typography variant="caption">{fieldReq.text}</Typography>
                            </Alert>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={
                                  typeof mapping.include === "string"
                                    ? mapping.include !== ""
                                    : Boolean(mapping.include)
                                }
                                onChange={(e) => {
                                  const includeFunction = e.target.checked
                                    ? mapping.isDefault
                                      ? "() => true"
                                      : "(e) => e"
                                    : "";
                                  onIngestionMappingChange(sourceKey, "include", includeFunction);
                                  onIngestionMappingChange(
                                    sourceKey,
                                    "includeFunction",
                                    includeFunction
                                  );
                                }}
                                disabled={mapping.isFixed}
                                color="primary"
                              />
                            }
                            label=""
                          />
                          {mapping.include && (
                            <Chip
                              size="small"
                              label="Included"
                              color="success"
                              variant="filled"
                              sx={{ fontSize: "0.7rem" }}
                            />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="textSecondary">
              Tip: Double-click a row to edit its modifier function.
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <ModifierModal
        open={modifierOpen}
        onClose={() => setModifierOpen(false)}
        onSubmit={handleModifierSubmit}
        modalState={modalState}
        onStateChange={handleModalStateChange}
      />
    </Box>
  );
};

export default IngestionMapper;
